/**
 * Centralized logging utility for the application
 * Provides structured logging with different levels and environment-aware output
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  data?: any;
  timestamp: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';

  private formatMessage(entry: LogEntry): string {
    const contextStr = entry.context ? `[${entry.context}]` : '';
    return `${entry.timestamp} ${contextStr} ${entry.message}`;
  }

  private createLogEntry(level: LogLevel, message: string, context?: string, data?: any): LogEntry {
    return {
      level,
      message,
      context,
      data,
      timestamp: new Date().toISOString()
    };
  }

  debug(message: string, context?: string, data?: any): void {
    if (!this.isDevelopment) return;
    
    const entry = this.createLogEntry('debug', message, context, data);
    console.debug(this.formatMessage(entry), data || '');
  }

  info(message: string, context?: string, data?: any): void {
    const entry = this.createLogEntry('info', message, context, data);
    console.info(this.formatMessage(entry), data || '');
  }

  warn(message: string, context?: string, data?: any): void {
    const entry = this.createLogEntry('warn', message, context, data);
    console.warn(this.formatMessage(entry), data || '');
  }

  error(message: string, context?: string, error?: any): void {
    const entry = this.createLogEntry('error', message, context, error);
    console.error(this.formatMessage(entry), error || '');
  }

  // Specialized methods for common use cases
  apiCall(url: string, method: string, data?: any): void {
    this.debug(`API ${method.toUpperCase()} ${url}`, 'API', data);
  }

  performance(operation: string, duration: number, context?: string): void {
    this.info(`${operation} completed in ${duration}ms`, context || 'PERF');
  }

  userAction(action: string, data?: any): void {
    this.info(`User action: ${action}`, 'USER', data);
  }
}

export const logger = new Logger();
export default logger;